import {
  Button,
  Card,
  Flex,
  Segmented,
  Spin,
  Table,
  TableColumnsType,
  Typography
} from "antd";
import { getWebMessages } from "api/services/MessageService";
import { TWebDMessage } from "api/services/models/message.types";
import { WebDPrivilege } from "api/services/models/WebDUser";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import dayjs from "dayjs";
import { ReactComponent as AddMessageIcon } from "Icons/vuesax/add-square.svg";
import { ReactComponent as UnReadIcon } from "Icons/vuesax/sms-selected.svg";
import { ReactComponent as ReadIcon } from "Icons/vuesax/sms.svg";
import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import MessageViewer from "./MessageViewer";
import NewMessage from "./NewMessage";
import utc from "dayjs/plugin/utc";
import { useLayoutContext } from "components/MainContainer";
import { Sheet } from "react-modal-sheet";

dayjs.extend(utc);

const makeColumns = (
  view: TMessageView,
  messages: TWebDMessage[]
): TableColumnsType<TWebDMessage> => {
  return [
    {
      dataIndex: "isRead",
      key: "status",
      width: "32px",
      render: status =>
        view === "Inbox" &&
        (status ? (
          <ReadIcon width="14px" height="14px" strokeWidth={1.5} />
        ) : (
          <UnReadIcon width="14px" height="14px" />
        ))
    },
    {
      title: `${view === "Inbox" ? "From" : "To"}`,
      minWidth: 244,
      width: "244px",
      dataIndex: `${view === "Inbox" ? "fromUserId" : "toUserId"}`,
      key: "user",
      showSorterTooltip: false,
      render: (_, m) =>
        view === "Inbox"
          ? `${m.fromUserId}@${m.fromAccountId}`
          : `${m.toUserId}@${m.toAccountId}`,
      sorter: (a, b) =>
        view === "Inbox"
          ? a.fromUserId.localeCompare(b.fromUserId)
          : a.toUserId.localeCompare(b.toUserId),
      filters: [
        ...new Set(
          messages
            .map(m => (view === "Inbox" ? m.fromUserId : m.toUserId))
            .toSorted((n1, n2) => n1.localeCompare(n2))
        )
      ].map(userId => ({
        text: userId,
        value: userId
      })),
      onFilter: (value, m) => (view === "Inbox" ? m.fromUserId : m.toUserId) === value
    },
    {
      title: "Message",
      dataIndex: "messageText",
      key: "messageText",
      ellipsis: { showTitle: true },
      render: value => <Typography.Text style={{ whiteSpace: "normal", wordBreak: "break-word"}}>{value}</Typography.Text>,
      sorter: false,
      minWidth: 160,
      width: 160
    },
    {
      title: "Created On",
      dataIndex: "createdDateTime",
      key: "datetime",
      minWidth: 160,
      width: 160,
      render: datetime => dayjs.utc(datetime).local().format("MM/DD/YYYY, hh:mm:ss A"),
      showSorterTooltip: false,
      sorter: (a, b) => (dayjs(a.createdDatetime).isBefore(dayjs(b.createdDatetime)) ? -1 : 1)
    }
  ];
};

type TMessageView = "Inbox" | "Sent";

type TMessageViewState = {
  show: boolean;
  message?: TWebDMessage;
};

const MessageGrid = forwardRef((props, ref) => {
  const { webDUser, accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [view, setView] = useState<TMessageView>("Inbox");
  const [messages, setMessages] = useState<TWebDMessage[]>([]);
  const [tableKey, setTableKey] = useState(0);
  const [messageViewerState, setMessageViewerState] = useState<TMessageViewState>({
    show: false
  });
  const [showNewMessageDialog, setShowNewMessageDialog] = useState(false);
  const { toggleDrawer, isSmallScreen } = useLayoutContext();
  const [open, setOpen] = useState(false);
  
  
  const handleCombinedClick = (item: TWebDMessage | undefined) => {
    setMessageViewerState({ show: true, message: item })
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openNewMessageDialog() {
      setShowNewMessageDialog(true);
    }
  }));

  const fetchMessages = () => {
    if (accountId && webDUser?.user.userId) {
      setTableKey(k => k + 1);
      setLoading(true);
      setError(null);
      getWebMessages(view) .then(fetchedMessages => {
        const sortedMessages = fetchedMessages.sort((a:any, b:any) => {
          const dateA = a.createdDateTime ? new Date(a.createdDateTime).getTime() : 0;
          const dateB = b.createdDateTime ? new Date(b.createdDateTime).getTime() : 0;
          return dateA - dateB;
        });
        setMessages(sortedMessages);
      })
        .catch(setError)
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => fetchMessages(), [accountId, view]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);

  const onChangeView = (v: TMessageView) => {
    setView(v);
    setMessageViewerState({ show: false });
  };

  const isSelected = (message: TWebDMessage) => {
    if (messageViewerState.message?.messageId) {
      return message.messageId === messageViewerState.message.messageId;
    }
    return false;
  };

  const onAddMessage = (newMessages?: TWebDMessage[]) => {
    setShowNewMessageDialog(false);
    if (newMessages?.length && view === "Sent") {
      setMessages(msgs => [...msgs, ...newMessages]);
    }
  };

  const onMessageRead = (message: TWebDMessage) => {
    setMessages(msgs =>
      msgs.map(m => ({ ...m, isRead: m.messageId === message.messageId ? true : m.isRead }))
    );
  };

  return (
    <>
      {!isSmallScreen ? (
        <>
          <Flex wrap gap="middle" justify="space-between" style={{ margin: "8px 0" }}>
            <Segmented<TMessageView>
              options={["Inbox", "Sent"]}
              onChange={onChangeView}
              size="large"
              className="segment"
            />
            <Button
              type="primary"
              className="default-button"
              disabled={!webDUser?.hasPrivilege(WebDPrivilege.CreateNewMessage)}
              icon={<AddMessageIcon className="default-icon" />}
              iconPosition={"start"}
              onClick={() => setShowNewMessageDialog(true)}
              title="New Message">
              New Message
            </Button>
          </Flex>
          {loading && (
            <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
              <Spin tip="Loading, please wait..." />
            </Flex>
          )}
          {error && <ErrorViewer error={error} />}
          {!loading && (
            <div className="flex-container">
              <Card
                className="card-style"
                styles={{ body: { padding: "0" } }}
                style={{ marginRight: messageViewerState.show && messageViewerState.message ? "1.25rem" : "0" }}>
                <Table<TWebDMessage>
                  key={tableKey}
                  style={{ border: "none" }}
                  className="table-style"
                  components={{
                    body: {
                      cell: (props: any) => (
                        <td
                          {...props}
                          style={{
                            borderBottomWidth: "1px",
                            borderBottomColor: "#f0f0f0",
                            borderBottomStyle: "solid"
                          }}
                        />
                      )
                    }
                  }}
                  dataSource={
                    messages
                    .sort((a, b) => (dayjs(a.createdDatetime).isBefore(dayjs(b.createdDatetime)) ? 1 : -1))
                  }
                  columns={makeColumns(view, messages)}
                  rowKey={"messageId"}
                  pagination={{
                    hideOnSinglePage: true,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 30, 40, 50]
                  }}
                  rowClassName={record =>
                    isSelected(record) ? "selected-row" : "unselected-row"
                  }
                  onRow={record => ({
                    onClick: () => setMessageViewerState({ show: true, message: record })
                  })}
                />
              </Card>
              {/* <Card className="message-card" style={{marginTop:"1.94rem", marginRight:"1.5rem"}}></Card> */}
              {messageViewerState.show && messageViewerState.message && (
                <MessageViewer
                  message={messageViewerState.message}
                  onMessageRead={onMessageRead}
                />
              )}
              {showNewMessageDialog && <NewMessage onClose={onAddMessage} />}
            </div>
          )}
        </>
      ) : (
        <>
          <Flex wrap gap="middle" justify="space-between" style={{ margin: "8px 0" }}>
            <Segmented<TMessageView>
              options={["Inbox", "Sent"]}
              onChange={onChangeView}
              size="large"
              className="segment"
              style={{ width: "100%" }}
            />
          </Flex>
          {loading && (
            <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
              <Spin tip="Loading, please wait..." />
            </Flex>
          )}
          {error && <ErrorViewer error={error} />}
          {!loading && (
            <div className="flex-container">
              <Flex
                wrap="wrap"
                gap="middle"
                justify="space-between"
                style={{ marginTop: "1.75rem" }}>
                {(() => {
                  if (loading) {
                    return (
                      <div style={{ textAlign: "center", width: "100%" }}>
                        <Spin size="large" />
                      </div>
                    );
                  }
                  if (error) {
                    return <ErrorViewer error={error} />;
                  }
                  if (messages.length === 0) {
                    return (
                      <Typography.Title
                        level={4}
                        style={{ textAlign: "center", width: "100%" }}>
                        No Messages Found
                      </Typography.Title>
                    );
                  }

                  return messages.map(item => (
                    <Card
                      key={item.messageId}
                      onClick={() => handleCombinedClick(item)}
                      styles={{ body: { padding: ".87rem .88rem" } }}
                      style={{ width: "calc(100vw - 3rem)", backgroundColor: "#FFF" }}>
                      <Flex gap="middle" align="center" justify="flex-start">
                        {view === "Inbox" &&
                          (item.isRead ? (
                            <ReadIcon width="14px" height="14px" strokeWidth={1.5} />
                          ) : (
                            <UnReadIcon width="14px" height="14px" />
                          ))}
                        <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
                          {view === "Inbox"
                            ? `${item.fromUserId}@${item.fromAccountId}`
                            : `${item.toUserId}@${item.toAccountId}`}
                        </Typography.Title>
                      </Flex>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: "16px"
                        }}>
                        <div
                          style={{
                            display: "flex",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            width: "100%"
                          }}>
                          <span className="font-777" style={{ textWrap: "nowrap" }}>
                            Date, Time:{" "}
                          </span>
                          <span
                            className="font-1212 font-1212-mobile"
                            style={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              width: "100%",
                              display: "inline-block"
                            }}>
                            {dayjs(item.createdDatetime).format("MM/DD/YYYY")}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            width: "100%"
                          }}>
                          <span
                            className="font-1212 font-1212-mobile"
                            style={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              width: "100%",
                              display: "inline-block"
                            }}>
                            {item.messageText}
                          </span>
                        </div>
                      </div>
                    </Card>
                  ));
                })()}
              </Flex>
            </div>
          )}
          <Sheet isOpen={open} onClose={() => setOpen(false)} detent={"content-height"}>
          <Sheet.Container>
            <Sheet.Header />
            <Sheet.Content style={{ padding: "1.25rem" }}>
              {messageViewerState.show && messageViewerState.message && (
                <MessageViewer
                  message={messageViewerState.message}
                  onMessageRead={onMessageRead}
                />
              )}
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop />
        </Sheet>
      {showNewMessageDialog &&  
      <div
        style={{
          position: 'fixed',
          top: 0 ,
          left: 0 ,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#FFF',
          zIndex: 9999,
          overflow: 'auto',
        }}
      ><NewMessage onClose={onAddMessage} />
      </div>}
        </>
      )}
    </>
  );
});

export default MessageGrid;
